import { APIResponseType } from "app/apiService/types";
import apiService from "app/apiService";
import queryClient from "app/apiService/queryClient";
import { verifyRunState } from "./runStateResponse";

export interface NurturingPreferenceRow {
  segment_id: string;
  segment_name: string;
  available_for_nurturing_candidate: number;
  batch_size: number;
  maiq_lead_type?: string;
  earliest_available_date?: string | null;
  earliestAvailableForNurturingDate?: string | null;
}

/** Unique key for a preference row (segment + lead type). */
export function getNurturingRowKey(row: NurturingPreferenceRow): string {
  const lead = row.maiq_lead_type ?? "";
  return lead ? `${row.segment_id}-${lead}` : row.segment_id;
}

export interface NurturingPreferencesProperties {
  ttl: number;
  sold_wait_days: number;
  unsold_wait_days: number;
  row: NurturingPreferenceRow[];
  /**
   * Saved date-filter settings for nurturing candidate picking.
   * These are optional to support older preference records.
   */
  dateFilterType?: PickDateFilterType | null;
  startDate?: string | null;
  endDate?: string | null;
  all?: boolean | null;
}

export interface NurturingPreferencesResponse {
  _id?: string;
  id?: string;
  properties: NurturingPreferencesProperties;
  scope?: string;
  header_row?: string[];
}

export type PickDateFilterType = "ALL" | "SINCE_DATE" | "DATE_RANGE";

export interface PickNurturingCandidatesPayloadOptions {
  preferences?: NurturingPreferencesResponse;
  startDate?: string;
  endDate?: string;
}

class RunLogsApiService {
  private static cacheVerifiedState(runId: string, response: APIResponseType<any>) {
    // A detail URL is a different query key from the list URL; prime both.
    queryClient.setQueryData(["GET", `api/v2/run-v2/${runId}`, {}], response);
    queryClient.setQueriesData<any>({
      predicate: query => query.queryKey[0] === "GET" && query.queryKey[1] === "api/v2/run-v2",
    }, cached => {
      if (!Array.isArray(cached?.body?.data)) return cached;
      return { ...cached, body: { ...cached.body, data: cached.body.data.map((row: any) =>
        String(row._id) === runId ? { ...row, ...response.body } : row
      ) } };
    });
    apiService.invalidateCacheByPrefix("api/v2/run-v2", "GET");
  }

  static async markRunState(
    runId: string,
    state: "failed" | "success",
    dealerId: string
  ): Promise<APIResponseType<any>> {
    const response = await apiService.put<any>(
      `api/v2/run-v2/${runId}/state`,
      { state },
      { params: { dealer_id: dealerId } },
      false
    );
    verifyRunState(response?.body, runId, state);
    this.cacheVerifiedState(runId, response);
    return response;
  }

  static async markActionState(
    runId: string,
    actionId: string,
    state: "failed" | "success",
    dealerId: string
  ): Promise<APIResponseType<any>> {
    const response = await apiService.put<any>(
      `api/v2/run-v2/${runId}/actions/${actionId}/state`,
      { state },
      { params: { dealer_id: dealerId } },
      false
    );
    verifyRunState(response?.body, runId, state, actionId);
    this.cacheVerifiedState(runId, response);
    return response;
  }

  static async retryAction(
    runId: string,
    actionId: string,
    dealerId: string
  ): Promise<APIResponseType<any>> {
    const response = await apiService.post<any>(
      `api/v2/run-v2/${runId}/actions/${actionId}/retry`,
      {},
      { params: { dealer_id: dealerId } },
      false
    );
    if (response?.body) response.body = { ...response.body, manual_state: undefined };
    await apiService.invalidateCacheByPrefix("api/v2/run-v2", "GET");
    return response;
  }

  private static readonly PREFERENCES_BASE = "api/v2/preferences";

  static readonly NURTURING_PREFERENCE_TYPE =
    "NURTURING_CANDIDATE_PICKER_PREFERENCES";

  /**
   * GET /preferences/:dealerId/NURTURING_CANDIDATE_PICKER_PREFERENCES
   * Pass refresh=true to bypass backend cache and force a fresh fetch.
   */
  static async getNurturingPreferences(
    dealerId: string,
    refresh?: boolean
  ): Promise<APIResponseType<NurturingPreferencesResponse>> {
    if (!dealerId) {
      return { body: null as any, message: "Dealer ID is required" };
    }
    const url = `${RunLogsApiService.PREFERENCES_BASE}/${dealerId}/${RunLogsApiService.NURTURING_PREFERENCE_TYPE}`;
    const config = refresh ? { params: { refresh_metrics: true } } : undefined;
    return apiService.get<NurturingPreferencesResponse>(url, config);
  }

  /**
   * POST /jobs/NURTURING_CANDIDATES_PICKER
   * Triggers the nurturing candidates picker job for a dealer.
   * Accepts the full preferences object (with edited values) to include in the payload.
   */
  static async pickNurturingCandidates(
    dealerId: string,
    options?: PickNurturingCandidatesPayloadOptions
  ): Promise<APIResponseType<any>> {
    if (!dealerId) {
      return { body: null as any, message: "Dealer ID is required" };
    }
    const url = "api/v2/jobs/NURTURING_CANDIDATES_PICKER";
    const body = {
      details: {
        dealer_id: dealerId,
        lead_types: ["SOLD", "UNSOLD"],
        date_window_days_unsold: 30,
        date_window_days_sold: 90,
        ...(options?.preferences
          ? { preferences: options.preferences.properties }
          : {}),
        ...(options?.startDate ? { startDate: options.startDate } : {}),
        ...(options?.endDate ? { endDate: options.endDate } : {}),
      },
    };
    return apiService.post<any>(url, body, undefined, false);
  }

  /**
   * PUT /preferences/:preferenceId
   * Body mirrors the GET response structure.
   */
  static async updateNurturingPreferences(
    preferenceId: string,
    body: Partial<NurturingPreferencesResponse>
  ): Promise<APIResponseType<NurturingPreferencesResponse>> {
    if (!preferenceId) {
      return { body: null as any, message: "Preference ID is required" };
    }
    const url = `${RunLogsApiService.PREFERENCES_BASE}/${preferenceId}`;
    return apiService.put<NurturingPreferencesResponse>(
      url,
      body,
      undefined,
      false
    );
  }

  /**
   * POST /run-v2/:id/retry?dealer_id=:dealerId
   * Retries a failed run log for a specific dealer.
   */
  static async retryRunLog(
    runLogId: string,
    dealerId: string
  ): Promise<APIResponseType<any>> {
    if (!runLogId) {
      return { body: null as any, message: "Run log ID is required" };
    }
    if (!dealerId) {
      return { body: null as any, message: "Dealer ID is required" };
    }

    const url = `api/v2/run-v2/${runLogId}/retry`;
    const response = await apiService.post<any>(
      url,
      {},
      { params: { dealer_id: dealerId } },
      false
    );
    if (response?.body) response.body = { ...response.body, manual_state: undefined };
    await apiService.invalidateCacheByPrefix("api/v2/run-v2", "GET");
    return response;
  }

  /**
   * GET /run-v2/:id/actions/:actionId/failed-records
   * Staging rows that failed inside a completed lead action.
   */
  static async getFailedRecords(
    runId: string,
    actionId: string,
    params?: { skip?: number; limit?: number }
  ): Promise<APIResponseType<any>> {
    if (!runId || !actionId) {
      return { body: null as any, message: "Run and action are required" };
    }
    const url = `api/v2/run-v2/${runId}/actions/${actionId}/failed-records`;
    const config = params ? { params } : undefined;
    apiService.invalidateCache(url, "GET", config);
    return apiService.get<any>(url, config);
  }

  /**
   * POST /run-v2/:id/actions/:actionId/retry-records
   * Retries selected failed staging rows, then the same clean for the run.
   */
  static async retryFailedRecords(
    runId: string,
    actionId: string,
    body: { ids?: string[]; all_failed?: boolean }
  ): Promise<APIResponseType<any>> {
    if (!runId || !actionId) {
      return { body: null as any, message: "Run and action are required" };
    }
    const url = `api/v2/run-v2/${runId}/actions/${actionId}/retry-records`;
    return apiService.post<any>(url, body, undefined, false);
  }
}

export default RunLogsApiService;
